import { useCallback, useRef, useState, useEffect } from 'react';
import { useReducedMotion } from 'framer-motion';
import throttle from 'lodash/throttle';

export const useSwimlane = (
  swimlaneDirection: 'horizontal' | 'vertical' = 'horizontal'
) => {
  const scrollContainerRef = useRef<HTMLUListElement | null>(null);
  const [contentLoaded, setContentLoaded] = useState<boolean>(false);
  const [showPreviousButton, setShowPreviousButton] = useState<boolean>(false);
  const [showNextButton, setShowNextButton] = useState<boolean>(true);

  // prevents scroll animation if reduced motion is preferred
  const shouldReduceMotion = useReducedMotion();

  // Next and Previous Scrolling
  const handleButtonClick = (direction: 'next' | 'previous') => {
    const { current } = scrollContainerRef;
    if (current) {
      const { scrollLeft, scrollTop, offsetHeight, scrollWidth, clientWidth } =
        current;

      // save width of item (this is static at all widths where the arrows are visible)
      //              poster  margin
      const itemWidth = 219 + 16;

      // how many items can fit within the total width?
      // this can be done at the page level and passed in as it should be calculated each time
      const itemsInContainer = (): number => {
        let numItems = 0;
        for (let i = 0; i + itemWidth <= clientWidth; i += itemWidth) {
          numItems++;
        }
        return numItems;
      };

      const totalItems = scrollWidth / itemWidth;

      // save itemsPerPage as variable so it doesn't need to be recalculated in below loop
      const itemsPerPage = itemsInContainer();

      const pageWidth = itemsPerPage * itemWidth;

      const getNextScroll = (direction: string) => {
        if (direction === 'next') {
          if (scrollLeft + pageWidth >= scrollWidth) {
            return totalItems - itemsPerPage;
          } else {
            return Math.floor((scrollLeft + pageWidth) / itemWidth);
          }
        } else {
          if (scrollLeft - pageWidth <= 0) {
            return 0;
          } else {
            return Math.floor((scrollLeft - pageWidth) / itemWidth);
          }
        }
      };

      // could this potentially be calculated at a higher level based on the screenwidth?
      const itemScrolls: number[] = [];

      // populate itemStarts array with beginning scrollLeft values
      for (let i = 0, p = 0; i < scrollWidth; i += itemWidth, p++) {
        if (i + itemsPerPage > scrollWidth) {
          itemScrolls[p] = itemScrolls[p - 1] + itemWidth;
        } else {
          itemScrolls[p] = i;
        }
      }

      if (direction === 'next') {
        current.scrollTo({
          top: scrollTop + offsetHeight,
          left: itemScrolls[getNextScroll('next')],
          behavior: shouldReduceMotion ? 'auto' : 'smooth',
        });
      }

      if (direction === 'previous') {
        current.scrollTo({
          top: scrollTop - offsetHeight,
          left: itemScrolls[getNextScroll('previous')],
          behavior: shouldReduceMotion ? 'auto' : 'smooth',
        });
      }
    }
  };

  // Tracks scroll progress and adds/removes arrows accordingly
  const handleScroll = useCallback(
    throttle((e) => {
      const {
        scrollLeft,
        scrollTop,
        scrollWidth,
        scrollHeight,
        offsetWidth,
        offsetHeight,
      } = e.target;

      if (scrollWidth === offsetWidth && offsetHeight === scrollHeight) {
        setShowPreviousButton(false);
        setShowNextButton(false);
      } else {
        const progress =
          swimlaneDirection === 'horizontal'
            ? (scrollLeft / (scrollWidth - offsetWidth)) * 100
            : (scrollTop / (scrollHeight - offsetHeight)) * 100;
        setShowPreviousButton(progress > 0);
        setShowNextButton(progress < 100);
      }
    }, 100),
    [scrollContainerRef]
  );

  useEffect(() => {
    scrollContainerRef.current?.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      if (scrollContainerRef?.current) {
        scrollContainerRef?.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [scrollContainerRef, handleScroll]);

  // Add initial directional arrows when list mounts
  useEffect(() => {
    setTimeout(() => {
      if (scrollContainerRef.current) {
        const { offsetWidth, offsetHeight, scrollWidth, scrollHeight } =
          scrollContainerRef.current;
        if (scrollWidth === offsetWidth && offsetHeight === scrollHeight) {
          setShowPreviousButton(false);
          setShowNextButton(false);
        }
      }
    }, 1000);
  }, [scrollContainerRef]);

  return {
    scrollContainerRef,
    handleScroll,
    showPreviousButton,
    showNextButton,
    handleButtonClick,
    contentLoaded,
    setContentLoaded,
  };
};
